import React, { useState, useRef, useEffect } from 'react';
import NearbyLandmarksModal from './NearbyLandmarksModal';
import { AnimatePresence } from 'framer-motion';
import SectionInfoBox from '../../../components/SectionInfoBox';

interface NearbyPlacesProps {
    project: any;
}

const CATEGORY_MAP: Record<string, { label: string; icon: string; emoji: string }> = {
    school: { label: 'Schools', icon: 'fa-graduation-cap', emoji: '🎓' },
    schools: { label: 'Schools', icon: 'fa-graduation-cap', emoji: '🎓' },
    education: { label: 'Education', icon: 'fa-graduation-cap', emoji: '🎓' },
    
    transport: { label: 'Bus Stops', icon: 'fa-bus', emoji: '🚌' },
    'bus stops': { label: 'Bus Stops', icon: 'fa-bus', emoji: '🚌' },
    'bus stop': { label: 'Bus Stops', icon: 'fa-bus', emoji: '🚌' },
    metro: { label: 'Metro Stations', icon: 'fa-train-subway', emoji: '🚇' },
    station: { label: 'Stations', icon: 'fa-train-subway', emoji: '🚉' },
    
    hospital: { label: 'Hospitals', icon: 'fa-hospital', emoji: '🏥' },
    hospitals: { label: 'Hospitals', icon: 'fa-hospital', emoji: '🏥' },
    medical: { label: 'Medical', icon: 'fa-hospital', emoji: '🏥' },
    
    bank: { label: 'Banks', icon: 'fa-building-columns', emoji: '🏦' },
    banks: { label: 'Banks', icon: 'fa-building-columns', emoji: '🏦' },
    
    temple: { label: 'Temples', icon: 'fa-place-of-worship', emoji: '🛕' },
    temples: { label: 'Temples', icon: 'fa-place-of-worship', emoji: '🛕' },
    mosque: { label: 'Mosques', icon: 'fa-mosque', emoji: '🕌' },
    mosques: { label: 'Mosques', icon: 'fa-mosque', emoji: '🕌' },
    church: { label: 'Churches', icon: 'fa-church', emoji: '⛪' },
    churches: { label: 'Churches', icon: 'fa-church', emoji: '⛪' },
    
    atm: { label: 'ATMs', icon: 'fa-money-bill-transfer', emoji: '🏧' },
    atms: { label: 'ATMs', icon: 'fa-money-bill-transfer', emoji: '🏧' },
    
    shopping: { label: 'Shopping', icon: 'fa-cart-shopping', emoji: '🛍️' },
    'shopping malls': { label: 'Shopping Malls', icon: 'fa-cart-shopping', emoji: '🛍️' },
    mall: { label: 'Shopping Malls', icon: 'fa-cart-shopping', emoji: '🛍️' },
    malls: { label: 'Shopping Malls', icon: 'fa-cart-shopping', emoji: '🛍️' },
    market: { label: 'Markets', icon: 'fa-store', emoji: '🏪' },
    
    parks: { label: 'Parks', icon: 'fa-tree', emoji: '🌳' },
    park: { label: 'Parks', icon: 'fa-tree', emoji: '🌳' },
    garden: { label: 'Gardens', icon: 'fa-leaf', emoji: '🏡' },
    gardens: { label: 'Gardens', icon: 'fa-leaf', emoji: '🏡' },
    
    'post offices': { label: 'Post Offices', icon: 'fa-envelope', emoji: '✉️' },
    'post office': { label: 'Post Office', icon: 'fa-envelope', emoji: '✉️' },
    
    'business hubs': { label: 'Business Hubs', icon: 'fa-briefcase', emoji: '💼' },
    'business hub': { label: 'Business Hubs', icon: 'fa-briefcase', emoji: '💼' },
    office: { label: 'Offices', icon: 'fa-briefcase', emoji: '💼' },
    offices: { label: 'Offices', icon: 'fa-briefcase', emoji: '💼' }
};

const getCategoryMetadata = (catName: string) => {
    const key = catName.toLowerCase().trim();
    if (CATEGORY_MAP[key]) {
        return CATEGORY_MAP[key];
    }
    return {
        label: catName,
        icon: 'fa-map-pin',
        emoji: '📍'
    };
};

const getFilteredPlaces = (catId: string, places: any[]) => {
    return places.filter((place: any) => {
        const cat = place.category || place.type || 'Landmark';
        return cat.toLowerCase().trim() === catId.toLowerCase().trim();
    });
};

const NearbyPlaces: React.FC<NearbyPlacesProps> = ({ project }) => {
    const advantages = (project.nearbyPlaces && project.nearbyPlaces.length > 0)
        ? project.nearbyPlaces
        : (project.locationAdvantages && project.locationAdvantages.length > 0)
            ? project.locationAdvantages
            : [
                { name: 'Apple Global School', distance: '1.2 KM', type: 'school', category: 'school' },
                { name: 'Jayadeva Metro Station', distance: '2.9 KM', type: 'transport', category: 'transport' },
                { name: 'City Hospital', distance: '3.0 KM', type: 'hospital', category: 'hospital' },
                { name: 'Central Mall', distance: '1.5 KM', type: 'shopping', category: 'shopping' },
                { name: 'State Bank of India', distance: '0.5 KM', type: 'bank', category: 'bank' },
                { name: 'Shiva Temple', distance: '0.8 KM', type: 'temple', category: 'temple' },
                { name: 'HDFC ATM', distance: '0.4 KM', type: 'atm', category: 'atm' },
            ];

    const visibleCategories = React.useMemo(() => {
        const uniqueNames = Array.from(
            new Set(advantages.map((place: any) => (place.category || place.type || 'Landmark') as string))
        ) as string[];
        return uniqueNames.map((name: string) => {
            const meta = getCategoryMetadata(name);
            return {
                id: name,
                label: meta.label,
                icon: meta.icon,
                emoji: meta.emoji
            };
        });
    }, [advantages]);

    const [activeCategory, setActiveCategory] = useState<string>(() => {
        return visibleCategories.length > 0 ? visibleCategories[0].id : 'school';
    });
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const [showAll, setShowAll] = useState(false); // ⭐ NEW

    useEffect(() => {
        if (visibleCategories.length > 0 && !visibleCategories.some(cat => cat.id === activeCategory)) {
            setActiveCategory(visibleCategories[0].id);
        }
    }, [visibleCategories, activeCategory]);

    const filteredPlaces = getFilteredPlaces(activeCategory, advantages);

    // ⭐ Show only 3 initially
    const visiblePlaces = showAll ? filteredPlaces : filteredPlaces.slice(0, 3);

    const scrollRef = useRef<HTMLDivElement>(null);
    const placesScrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [canPlacesScrollLeft, setCanPlacesScrollLeft] = useState(false);
    const [canPlacesScrollRight, setCanPlacesScrollRight] = useState(false);

    const checkScroll = (ref: React.RefObject<HTMLDivElement>, setLeft: React.Dispatch<React.SetStateAction<boolean>>, setRight: React.Dispatch<React.SetStateAction<boolean>>) => {
        if (ref.current) {
            const { scrollLeft, scrollWidth, clientWidth } = ref.current;
            setLeft(scrollLeft > 5);
            setRight(scrollLeft + clientWidth < scrollWidth - 5);
        }
    };

    useEffect(() => {
        const catEl = scrollRef.current;
        const placeEl = placesScrollRef.current;

        const handleCatScroll = () => checkScroll(scrollRef, setCanScrollLeft, setCanScrollRight);
        const handlePlaceScroll = () => checkScroll(placesScrollRef, setCanPlacesScrollLeft, setCanPlacesScrollRight);

        if (catEl) {
            handleCatScroll();
            catEl.addEventListener('scroll', handleCatScroll);
        }
        if (placeEl) {
            handlePlaceScroll();
            placeEl.addEventListener('scroll', handlePlaceScroll);
        }

        window.addEventListener('resize', () => {
            handleCatScroll();
            handlePlaceScroll();
        });

        return () => {
            catEl?.removeEventListener('scroll', handleCatScroll);
            placeEl?.removeEventListener('scroll', handlePlaceScroll);
            window.removeEventListener('resize', handleCatScroll);
        };
    }, [advantages, activeCategory]);

    const scroll = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
        if (ref.current) {
            const { scrollLeft, clientWidth } = ref.current;
            const scrollAmount = clientWidth * 0.8;
            ref.current.scrollTo({
                left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <section id="nearby-landmarks" className="mb-4 md:mb-6 scroll-mt-32">
            <div className="bg-white rounded-xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden">

                {/* Header */}
                <div className="p-3 pb-2 md:p-5 md:pb-2">
                    <h2 className="text-xl md:text-2xl font-bold text-black leading-tight flex items-center">
                        Nearby Landmarks - {project.name}
                        <SectionInfoBox text={project.sectionInfo?.nearby} />
                    </h2>
                    <div className="h-[1px] bg-slate-100 w-full mt-3 md:mt-4"></div>
                </div>

                {/* Map */}
                <div className="px-4 md:px-8 mb-6 md:mb-8 mt-1 md:mt-2">
                    <div className="relative h-32 md:h-48 w-full bg-slate-50 rounded-lg overflow-hidden border border-slate-100 group">
                        <div className="absolute inset-0 bg-no-repeat bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                            style={{ backgroundImage: "url('/assets/img/map-bg.jpg')" }}>
                        </div>
                        <div className="absolute inset-0 bg-black/5 backdrop-blur-[2px] transition-all group-hover:backdrop-blur-0 group-hover:bg-transparent"></div>

                        <div className="absolute inset-0 flex items-center justify-center">
                            <button
                                onClick={() => setIsMapModalOpen(true)}
                                className="bg-white text-[#001b4a] px-6 md:px-10 py-3 md:py-4 rounded-lg md:rounded-xl flex items-center gap-2 md:gap-3 shadow-xl border-2 border-[#001b4a] hover:bg-slate-50 hover:text-black hover:border-black transition-all font-bold text-[11px] active:scale-95 group/btn"
                            >
                                <i className="fa-solid fa-map-location-dot text-xs md:text-sm text-[#001b4a] group-hover/btn:text-black group-hover/btn:scale-125 transition-transform"></i>
                                View on Map
                            </button>
                        </div>
                    </div>
                </div>

                {/* Categories */}
                <div className="px-4 md:px-8 mb-4 relative group">
                    {/* Left Scroll Button */}
                    {canScrollLeft && (
                        <button 
                            onClick={() => scroll(scrollRef, 'left')}
                            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-lg border border-slate-100 text-slate-400 hover:text-[#001b4a] transition-all active:scale-90"
                            title="Scroll Left"
                        >
                            <i className="fa-solid fa-chevron-left text-[10px]"></i>
                        </button>
                    )}

                    <div 
                        ref={scrollRef}
                        className="flex gap-2 overflow-x-auto pb-2 no-scrollbar scroll-smooth"
                    >
                        {visibleCategories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => {
                                    setActiveCategory(cat.id);
                                    setShowAll(false);
                                }}
                                className={`px-3 md:px-5 py-2 md:py-3 rounded-lg flex items-center gap-1.5 md:gap-2 text-[13px] md:text-[14px] font-black transition-all whitespace-nowrap ${activeCategory === cat.id
                                        ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] border border-[#f1ae24] shadow-lg scale-105 ring-1 ring-[#f1ae24]/20'
                                        : 'bg-white border border-slate-200 text-[#05144b] hover:border-[#05144b]/20 opacity-70 hover:opacity-100'
                                    }`}
                            >
                                <span className="text-[12px] md:text-[14px]">{cat.emoji}</span>
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    {/* Right Scroll Button */}
                    {canScrollRight && (
                        <button 
                            onClick={() => scroll(scrollRef, 'right')}
                            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white shadow-lg border border-slate-100 text-slate-400 hover:text-[#001b4a] transition-all active:scale-90"
                            title="Scroll Right"
                        >
                            <i className="fa-solid fa-chevron-right text-[10px]"></i>
                        </button>
                    )}
                </div>

                {/* Landmarks Horizontal Scroll */}
                <div className="px-4 md:px-6 mb-2 relative group/list">
                    
                    <div className="mb-3 px-1 md:px-2">
                        <p className="text-[11px] font-medium text-black flex items-center gap-2">
                            <span className="w-4 h-[1px] bg-black/20"></span>
                            {visibleCategories.find(c => c.id === activeCategory)?.label}
                        </p>
                        <h3 className="text-lg md:text-xl font-bold text-black mt-0.5">
                            Nearby {project.name}
                        </h3>
                    </div>

                    {/* Navigation Buttons */}
                    {canPlacesScrollLeft && (
                        <button 
                            onClick={() => scroll(placesScrollRef, 'left')}
                            className="absolute left-2 top-[60%] -translate-y-1/2 z-10 w-9 md:w-10 h-9 md:h-10 flex items-center justify-center rounded-full bg-white shadow-xl border border-slate-100 text-[#001b4a] hover:scale-110 transition-all active:scale-90"
                        >
                            <i className="fa-solid fa-arrow-left text-sm md:text-base"></i>
                        </button>
                    )}
                    
                    {canPlacesScrollRight && (
                        <button 
                            onClick={() => scroll(placesScrollRef, 'right')}
                            className="absolute right-2 top-[60%] -translate-y-1/2 z-10 w-9 md:w-10 h-9 md:h-10 flex items-center justify-center rounded-full bg-white shadow-xl border border-slate-100 text-[#001b4a] hover:scale-110 transition-all active:scale-90"
                        >
                            <i className="fa-solid fa-arrow-right text-sm md:text-base"></i>
                        </button>
                    )}

                    <div 
                        ref={placesScrollRef}
                        className="flex gap-3 md:gap-4 overflow-x-auto pb-2 no-scrollbar scroll-smooth snap-x snap-mandatory px-0.5"
                    >
                        {filteredPlaces.length > 0 ? (
                            filteredPlaces.map((place: any, idx: number) => (
                                <div 
                                    key={idx} 
                                    className="min-w-[200px] md:min-w-[260px] bg-white rounded-lg md:rounded-xl pt-3 pb-2 md:pt-4 md:px-5 md:pb-3 px-4 shadow-[0_15px_35px_-10px_rgba(0,27,74,0.15)] border border-slate-100 hover:border-[#001b4a]/20 hover:shadow-[0_20px_40px_-10px_rgba(0,27,74,0.2)] transition-all group snap-start"
                                >
                                    <div className="flex justify-between items-center mb-3">
                                        <div className="group-hover:scale-125 transition-transform flex items-center justify-center text-3xl md:text-4xl">
                                            {visibleCategories.find(c => c.id === activeCategory)?.emoji || '📍'}
                                        </div>
                                        <div className="text-[12px] md:text-[13px] font-black bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-[#f1ae24] px-4 md:px-5 py-1.5 rounded-full shadow-lg border border-[#f1ae24]/30">
                                            {place.distance ? (String(place.distance).toLowerCase().includes('km') ? place.distance : `${place.distance} KM`) : '0.5 KM'}
                                        </div>
                                    </div>

                                    <h4 className="text-[15px] md:text-[17px] font-bold text-black leading-tight group-hover:translate-x-1 transition-transform truncate">
                                        {place.name}
                                    </h4>
                                </div>
                            ))
                        ) : (
                            <div className="w-full py-8 md:py-12 text-center text-black/40 font-medium text-[12px]">
                                No landmarks found in this category
                            </div>
                        )}
                    </div>
                </div>

            </div>

            <AnimatePresence>
                {isMapModalOpen && (
                    <NearbyLandmarksModal
                        isOpen={isMapModalOpen}
                        onClose={() => setIsMapModalOpen(false)}
                        project={project}
                        landmarks={advantages}
                        categories={visibleCategories}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

export default NearbyPlaces;